'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { LayoutDashboard, Megaphone, CalendarDays, BarChart3, Users, Settings, ChevronLeft, ChevronRight, Zap, Layers } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


interface NavItem {
  id: string;
  label: string;
  href: string;
  icon: React.ElementType;
  badge?: number;
  group: string;
}

const navItems: NavItem[] = [
  { id: 'nav-dashboard', label: 'Dashboard', href: '/', icon: LayoutDashboard, group: 'main' },
  { id: 'nav-campaigns', label: 'Ad Campaigns', href: '/ad-campaign-management', icon: Megaphone, badge: 3, group: 'main' },
  { id: 'nav-calendar', label: 'Content Calendar', href: '/content-calendar', icon: CalendarDays, badge: 7, group: 'main' },
  { id: 'nav-analytics', label: 'Page Analytics', href: '/page-analytics', icon: BarChart3, group: 'main' },
  { id: 'nav-flyer', label: 'Flyer Builder', href: '/flyer-builder', icon: Layers, group: 'main' },
  { id: 'nav-clients', label: 'Clients & Teams', href: '/client-team-management', icon: Users, group: 'manage' },
  { id: 'nav-settings', label: 'Settings', href: '/settings', icon: Settings, group: 'manage' },
];

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export default function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const pathname = usePathname();

  const isActive = (href: string) => {
    if (href === '/') return pathname === '/';
    return pathname.startsWith(href);
  };

  const mainItems = navItems.filter((i) => i.group === 'main');
  const manageItems = navItems.filter((i) => i.group === 'manage');

  return (
    <aside
      className={`
        fixed left-0 top-0 h-full z-30 flex flex-col bg-card border-r border-border sidebar-transition
        ${collapsed ? 'w-16' : 'w-60'}
      `}
    >
      {/* Logo */}
      <div className={`flex items-center h-16 border-b border-border px-3 ${collapsed ? 'justify-center' : 'gap-3'}`}>
        <AppLogo size={32} />
        {!collapsed && (
          <span className="font-extrabold text-lg tracking-tight text-foreground">AdHive</span>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto scrollbar-thin py-4 px-2">
        {/* Main */}
        {!collapsed && (
          <p className="section-header px-3 mb-2">Main</p>
        )}
        <ul className="space-y-0.5 mb-4">
          {mainItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.href);
            return (
              <li key={item.id}>
                <Link
                  href={item.href}
                  className={`
                    flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-150 relative group
                    ${active ? 'nav-item-active' : 'nav-item-inactive'}
                    ${collapsed ? 'justify-center' : ''}
                  `}
                  title={collapsed ? item.label : undefined}
                >
                  <Icon size={18} className="shrink-0" />
                  {!collapsed && <span className="flex-1">{item.label}</span>}
                  {!collapsed && item.badge && (
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold">
                      {item.badge}
                    </span>
                  )}
                  {collapsed && item.badge && (
                    <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-primary" />
                  )}
                  {/* Tooltip for collapsed */}
                  {collapsed && (
                    <span className="absolute left-full ml-2 px-2 py-1 bg-foreground text-background text-xs rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none z-50">
                      {item.label}
                    </span>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>

        {/* Manage */}
        {!collapsed && (
          <p className="section-header px-3 mb-2">Manage</p>
        )}
        {collapsed && <div className="border-t border-border my-2" />}
        <ul className="space-y-0.5">
          {manageItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.href);
            return (
              <li key={item.id}>
                <Link
                  href={item.href}
                  className={`
                    flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-150 relative group
                    ${active ? 'nav-item-active' : 'nav-item-inactive'}
                    ${collapsed ? 'justify-center' : ''}
                  `}
                  title={collapsed ? item.label : undefined}
                >
                  <Icon size={18} className="shrink-0" />
                  {!collapsed && <span className="flex-1">{item.label}</span>}
                  {collapsed && (
                    <span className="absolute left-full ml-2 px-2 py-1 bg-foreground text-background text-xs rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none z-50">
                      {item.label}
                    </span>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Upgrade Prompt */}
      {!collapsed && (
        <div className="mx-2 mb-3 p-3 rounded-xl bg-primary/5 border border-primary/20">
          <div className="flex items-center gap-2 mb-1">
            <Zap size={14} className="text-primary" />
            <span className="text-xs font-semibold text-primary">Agency Pro</span>
          </div>
          <p className="text-xs text-muted-foreground mb-2">Unlock unlimited clients and advanced analytics.</p>
          <button className="w-full btn-primary text-xs py-1.5 justify-center">Upgrade Plan</button>
        </div>
      )}

      {/* User */}
      <div className={`border-t border-border p-3 flex items-center gap-3 ${collapsed ? 'justify-center' : ''}`}>
        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
          <span className="text-xs font-bold text-primary">MR</span>
        </div>
        {!collapsed && (
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-foreground truncate">Marcus Rivera</p>
            <p className="text-xs text-muted-foreground truncate">Admin</p>
          </div>
        )}
      </div>

      {/* Toggle Button */}
      <button
        onClick={onToggle}
        className="absolute -right-3 top-20 w-6 h-6 rounded-full bg-card border border-border flex items-center justify-center shadow-sm hover:bg-muted transition-colors duration-150 z-40"
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
      </button>
    </aside>
  );
}